<template>
    <section class="flex flex-col items-stretch mb-4 px-4 md:px-8 bg-white text-zinc-900 dark:bg-black dark:text-zinc-100">
        <div class="flex flex-wrap justify-center gap-4 w-full">
            <div class="grid items-center min-w-[220px] flex-1 md:flex-none">
                <label class="text-left" for="year">Año</label>
                <Dropdown 
                    id="year" 
                    v-model="selectedYear" 
                    :options="years" 
                    optionLabel="name" 
                    placeholder="Selecciona un año" 
                    class="w-full"
                    @change="fillTable" 
                    :disabled="disabled"
                />
            </div>
            <div class="grid items-center min-w-[220px] flex-1 md:flex-none">
                <label class="text-left" for="company">Empresa</label>
                <Dropdown 
                    id="company" 
                    v-model="selectedCompany" 
                    :options="companies" 
                    optionLabel="name" 
                    placeholder="Selecciona una empresa" 
                    class="w-full"
                    @change="companyChange" 
                    :disabled="disabled"
                />
            </div>
            <div class="grid items-center min-w-[220px] flex-1 md:flex-none">
                <label class="text-left" for="branch">Sucursal</label>
                <Dropdown 
                    id="branch" 
                    v-model="selectedBranch" 
                    :options="selectedCompany.branches" 
                    optionLabel="name" 
                    placeholder="Selecciona una sucursal" 
                    class="w-full" 
                    @change="fillTable" 
                    :disabled="disabled" 
                    filter 
                />
            </div>
            <div class="grid items-center min-w-[220px] flex-1 md:flex-none">
                <label class="text-left" for="extraordinary">Extraordinario</label>
                <ToggleButton 
                    id="extraordinary" 
                    v-model="isExtraordinary" 
                    onIcon="pi pi-check" 
                    offIcon="pi pi-times" 
                    onLabel="Sí"
                    aria-label="Extraordinario" 
                    class="w-full" 
                    @change="fillTable" 
                    :disabled="disabled"
                />
            </div>
            <div class="grid items-center min-w-[220px] flex-1 md:flex-none">
                <label class="text-left" for="external">Externo</label>
                <ToggleButton 
                    id="external" 
                    v-model="isExternal" 
                    onIcon="pi pi-check" 
                    offIcon="pi pi-times" 
                    onLabel="Sí"
                    aria-label="Externo" 
                    class="w-full" 
                    @change="fillTable" 
                    :disabled="disabled"
                />
            </div>
        </div>
    </section>

    <section class="grid justify-center pb-8 px-4 md:px-8" v-show="financeData.length > 0">
        <div class="card overflow-auto bg-white text-zinc-900 dark:bg-zinc-900 dark:text-zinc-100 rounded shadow">
            <DataTable 
                :value="financeData" 
                stripedRows 
                showGridlines 
                tableStyle="min-width: 30rem" 
                class="finance-dt min-w-full main-finance-table" 
                @row-click="onRowClick" 
                id="main-finance-table"
            >
                <Column field="month" bodyClass="month-column"></Column>
                <Column field="facturacionCobrada" header="Facturación cobrada"></Column>
                <Column field="gastosPagados" header="Gastos pagados"></Column>
                <Column field="utilidadFlujo" header="Utilidad de flujo"></Column>
            </DataTable>
        </div>
    </section>

    <section class="grid justify-center pb-8 px-4 md:px-8" v-show="prevYearDiffFinanceData.length > 0">
        <div class="card overflow-auto bg-white text-zinc-900 dark:bg-black dark:text-zinc-100">
            <h2 class="text-zinc-900 dark:text-zinc-100 text-left">Diferencia contra el año anterior</h2>
            <DataTable 
                :value="prevYearDiffFinanceData" 
                stripedRows 
                showGridlines 
                tableStyle="min-width: 30rem" 
                class="finance-dt min-w-full diff-finance-table"
            >
                <Column field="month" bodyClass="month-column"></Column>
                <Column field="facturacionCobrada" header="Facturación cobrada">
                    <template #body="{data, field}">
                        <span :class="{'green-cell': data[field].color === 'good', 'red-cell': data[field].color === 'bad'}">
                            {{ data[field].value }}
                        </span>
                    </template>
                </Column>
                <Column field="gastosPagados" header="Gastos pagados">
                    <template #body="{data, field}">
                        <span :class="{'green-cell': data[field].color === 'good', 'red-cell': data[field].color === 'bad'}">
                            {{ data[field].value }}
                        </span>
                    </template>
                </Column>
                <Column field="utilidadFlujo" header="Utilidad de flujo">
                    <template #body="{data, field}">
                        <span :class="{'green-cell': data[field].color === 'good', 'red-cell': data[field].color === 'bad'}">
                            {{ data[field].value }}
                        </span>
                    </template>
                </Column>
            </DataTable>
        </div>
    </section>

    <section class="grid justify-center px-4 md:px-8" v-show="prevYearDiffPercFinanceData.length > 0">
        <div class="card overflow-auto bg-white text-zinc-900 dark:bg-black dark:text-zinc-100">
            <h2 class="text-zinc-900 dark:text-zinc-100 text-left">Diferencia porcentual contra el año anterior</h2>
            <DataTable 
                :value="prevYearDiffPercFinanceData" 
                stripedRows 
                showGridlines 
                tableStyle="min-width: 30rem" 
                class="finance-dt min-w-full diff-perc-finance-table"
            >
                <Column field="month" bodyClass="month-column"></Column>
                <Column field="facturacionCobrada" header="Facturación cobrada">
                    <template #body="{data, field}">
                        <span :class="{'green-cell': data[field].color === 'good', 'red-cell': data[field].color === 'bad'}">
                            {{ data[field].value }}
                        </span>
                    </template>
                </Column>
                <Column field="gastosPagados" header="Gastos pagados">
                    <template #body="{data, field}">
                        <span :class="{'green-cell': data[field].color === 'good', 'red-cell': data[field].color === 'bad'}">
                            {{ data[field].value }}
                        </span>
                    </template>
                </Column>
                <Column field="utilidadFlujo" header="Utilidad de flujo">
                    <template #body="{data, field}">
                        <span :class="{'green-cell': data[field].color === 'good', 'red-cell': data[field].color === 'bad'}">
                            {{ data[field].value }}
                        </span>
                    </template>
                </Column>
            </DataTable>
        </div>
    </section>

    <Dialog v-model:visible="showInvoicesDialog" modal :style="{ width: '80rem' }" class="bg-white text-zinc-900 dark:bg-zinc-900 dark:text-zinc-100">
        <div>
            <h1 class="text-zinc-900 dark:text-zinc-100 text-center text-xl">Análisis de facturación</h1>
        </div>
        <div>
            <h2 class="text-zinc-700 dark:text-zinc-300 text-center text-sm">{{ subtitle }}</h2>
        </div>
        <div class="card overflow-auto text-zinc-900 dark:text-zinc-100">
            <div class="flex items-center gap-4 mb-4">
                <div class="w-4 h-4 bg-green-500 rounded ml-6"></div>
                <span>Pagada</span>
                <div class="w-4 h-4 bg-orange-400 rounded ml-6"></div>
                <span>Pago parcial</span>
                <div class="w-4 h-4 bg-red-500 rounded ml-6"></div>
                <span>Sin pagar</span>
            </div>
            <DataTable 
                :value="invoices" 
                stripedRows 
                showGridlines 
                tableStyle="min-width: 30rem" 
                class="min-w-full invoices-dialog-table" 
                scrollable
                scrollHeight="650px" 
                id="invoices-table"
            >
                <Column field="serial" header="Folio">
                    <template #body="{data}">
                        <span :class="{'green-cell': data.level === 0, 'orange-cell': data.level === 1, 'red-cell': data.level === 2}">
                            {{ data.serial }}
                        </span>
                    </template>
                </Column>
                <Column field="period" header="Emisión" :bodyStyle="{ minWidth: '120px'}" :headerStyle="{ minWidth: '120px' }"></Column>
                <Column field="name" header="Razón Social"></Column>
                <Column field="subtotal" header="Subtotal"></Column>
                <Column field="tax" header="IVA"></Column>
                <Column field="total" header="Total"></Column>
            </DataTable>
        </div>
    </Dialog>
</template>

<script>
import axiosInstance from '../axiosInstance';

export default {
    data() {
        return {
            selectedYear: {},
            years: [],
            selectedCompany: {},
            companies: [],
            selectedBranch: {},
            isExtraordinary: false,
            financeData: [],
            prevYearDiffFinanceData: [],
            prevYearDiffPercFinanceData: [],
            disabled: false,
            invoices: [],
            subtitle: '',
            showInvoicesDialog: false,
            isExternal: false
        }
    },
    methods: {
        async fetchInitialData() {
            this.$emit("update-loading", true);
            this.disabled = true;
            let data = {};
            
            try {
                const finances = axiosInstance.post("/api/finances", {
                    token: localStorage.getItem('token')
                });
                data = await finances;
            } catch(error) {
                console.error("fetchInitialData: There was an error!", error);
                this.$toast.add({ 
                    severity: 'error', 
                    summary: 'Error',
                    detail: 'Hubo un problema al consultar los datos iniciales.', 
                    life: 5000, 
                    group: 'topr' 
                });
            } finally {
                this.$emit("update-loading", false);
            }

            return data;
        },

        async fetchFinanceData() {
            this.$emit("update-loading", true);
            this.disabled = true;
            let data = {};
            
            try {
                const financesData = axiosInstance.post("/api/finances_data", {
                    token: localStorage.getItem('token'),
                    year: Number(this.selectedYear.name),
                    branch_id: this.selectedBranch.id,
                    company_id: this.selectedCompany.id,
                    show_extra: this.isExtraordinary,
                    show_external: this.isExternal
                });
                data = await financesData;
            } catch(error) {
                console.error("fetchFinanceData: There was an error!", error);
                this.$toast.add({ 
                    severity: 'error', 
                    summary: 'Error',
                    detail: 'Hubo un problema al consultar los datos de las tablas.', 
                    life: 5000, 
                    group: 'topr' 
                });
            } finally {
                this.$emit("update-loading", false);
                this.disabled = false;
            }

            return data;
        },

        setInitialValues() {
            this.fetchInitialData().then((response) => {
                if(response.status === 200) {
                    const years = response.data.years;
                    this.years = years.map((year, index) => {
                        return { name: year, value: index }
                    });
                    this.selectedYear = this.years[0];

                    const defaultCmp = response.data.defaultCmp;
                    this.selectedCompany = {
                        name: defaultCmp.data.name, 
                        id: defaultCmp.data.id, 
                        branches: defaultCmp.branches
                    }

                    this.companies = response.data.companies.map((company) => {
                        return {
                            name: company.data.name,
                            id: company.data.id,
                            branches: company.branches
                        }
                    });

                    this.selectedBranch = this.selectedCompany.branches[0];
                    this.fillTable();
                    this.disabled = false;
                } else {
                    console.warn("setInitialValues: There was a problem with the request.", response);
                    this.$toast.add({ 
                        severity: 'warn', 
                        summary: 'Aviso',
                        detail: 'Hubo un problema con la solicitud. Vuelve a intentarlo.', 
                        life: 5000, 
                        group: 'topr' 
                    });
                }
            });
        },

        fillTable() {
            this.fetchFinanceData().then((response) => {
                if(response.status == 200) {
                    const { months, old_months: oldMonths, old_percents: oldPercents } = response.data.data;

                    this.financeData = Object.values(months).map((month) => {
                        return {
                            month: month.label,
                            facturacionCobrada: month.paidF,
                            gastosPagados: month.expensesF,
                            utilidadFlujo: month.flowF
                        }
                    });

                    this.prevYearDiffFinanceData = Object.values(oldMonths).map((month) => {
                        return {
                            month: month.label,
                            facturacionCobrada: { value: month.paidDiffF, color: month.paidColor },
                            gastosPagados: { value: month.expensesDiffF, color: month.expensesColor },
                            utilidadFlujo: { value: month.flowDiffF, color: month.flowColor }
                        }
                    });
                    
                    this.prevYearDiffPercFinanceData = Object.values(oldPercents).map((month) => {
                        return {
                            month: month.label,
                            facturacionCobrada: { value: `${month.paidDiffF}%`, color: month.paidColor },
                            gastosPagados: { value: `${month.expensesDiffF}%`, color: month.expensesColor },
                            utilidadFlujo: { value: `${month.flowDiffF}%`, color: month.flowColor }
                        }
                    });
                } else {
                    console.warn("fillTable: There was a problem with the request.", response);
                    this.$toast.add({ 
                        severity: 'warn', 
                        summary: 'Aviso',
                        detail: 'Hubo un problema con la solicitud. Vuelve a intentarlo.', 
                        life: 5000, 
                        group: 'topr' 
                    });
                }
            });
        },

        async fetchInvoicesData(fromDate, toDate) {
            this.$emit("update-loading", true);
            this.disabled = true;
            let data = {};
            
            try {
                const response = axiosInstance.post("/api/invoices_viewer_data", {
                    from: this.formatDate(fromDate),
                    to: this.formatDate(toDate),
                    company_id: this.selectedCompany.id,
                    token: localStorage.getItem('token')
                });
                data = await response;
            } catch(error) {
                console.error("fetchInvoicesData: There was an error!", error);
                this.$toast.add({ 
                    severity: 'error', 
                    summary: 'Error',
                    detail: 'Hubo un problema al consultar los gastos.', 
                    life: 5000, 
                    group: 'topr' 
                });
            } finally {
                this.$emit("update-loading", false);
                this.disabled = false;
            }

            return data;
        },

        onRowClick(event) {
            const fromDate = new Date();
            fromDate.setMonth(event.index);
            fromDate.setDate(1);
            
            const toDate = new Date();
            toDate.setMonth(event.index + 1);
            toDate.setDate(0);

            this.fetchInvoicesData(fromDate, toDate).then((response) => {
                if(response.status == 200) {
                    const { invoices, subtotal, tax, total, subtitle } = response.data;

                    this.invoices = Object.values(invoices).map(({invoice_period, level, name, serial_str, subtotal, tax, total}) => {
                        return {
                            period: invoice_period,
                            level,
                            name,
                            serial: serial_str,
                            subtotal,
                            tax,
                            total
                        }
                    });

                    const totalData = {
                        serial: "",
                        period: "",
                        name: "",
                        subtotal,
                        tax,
                        total
                    }

                    this.invoices.push(totalData);
                    this.subtitle = subtitle;
                    this.showInvoicesDialog = true;
                } else {
                    console.warn("onRowClick: There was a problem with the request.", response);
                    this.$toast.add({ 
                        severity: 'warn', 
                        summary: 'Aviso',
                        detail: 'Hubo un problema con la solicitud. Vuelve a intentarlo.', 
                        life: 5000, 
                        group: 'topr' 
                    });
                }
            });
        },

        companyChange() {
            this.selectedBranch = this.selectedCompany.branches[0];
            this.fillTable();
        },

        formatDate(date) {
            const month = ((date.getMonth() + 1).toString()).padStart(2, "0");
            const day = ((date.getDate()).toString()).padStart(2, "0");
            return `${date.getFullYear()}-${month}-${day}`
        }
    },
    mounted() {
        this.setInitialValues();
    }
}
</script>

<style scoped>
::v-deep(.p-datatable) {
    border-radius: 0.5rem !important;
    overflow: hidden !important;
    box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1) !important;
}

::v-deep(.p-datatable tbody tr td) {
    text-align: right !important;
}

/* First table - main-finance-table */
::v-deep(.main-finance-table.p-datatable .p-datatable-thead > tr > th) {
    background-color: #0f1338 !important;
    color: white !important;
}

::v-deep(.main-finance-table.p-datatable .p-datatable-tbody > tr:nth-child(odd) > td) {
    background-color: #f3f4f6 !important;
    color: #111827 !important;
}

::v-deep(.main-finance-table.p-datatable .p-datatable-tbody > tr:nth-child(even) > td) {
    background-color: #ffffff !important;
    color: #111827 !important;
}

::v-deep(.main-finance-table.p-datatable .p-datatable-tbody > tr > td:first-child) {
    background-color: #0f1338 !important;
    color: white !important;
    font-weight: 600 !important;
    text-align: left !important;
}

::v-deep(#main-finance-table .p-datatable-tbody > tr) {
    cursor: pointer !important;
    transition: all 0.3s !important;
}

::v-deep(#main-finance-table .p-datatable-tbody > tr:hover > td:not(:first-child)) {
    background-color: #71717a !important;
    color: #f4f4f5 !important;
}

/* Second table - diff-finance-table */
::v-deep(.diff-finance-table.p-datatable .p-datatable-thead > tr > th) {
    background-color: #0f1338 !important;
    color: white !important;
}

::v-deep(.diff-finance-table.p-datatable .p-datatable-tbody > tr:nth-child(odd) > td) {
    background-color: #f3f4f6 !important;
    color: #111827 !important;
}

::v-deep(.diff-finance-table.p-datatable .p-datatable-tbody > tr:nth-child(even) > td) {
    background-color: #ffffff !important;
    color: #111827 !important;
}

::v-deep(.diff-finance-table.p-datatable .p-datatable-tbody > tr > td:first-child) {
    background-color: #0f1338 !important;
    color: white !important;
    font-weight: 600 !important;
    text-align: left !important;
}

::v-deep(.diff-finance-table.p-datatable .p-datatable-tbody > tr > td span.green-cell) {
    color: #16a34a !important;
    font-weight: 600;
}

::v-deep(.diff-finance-table.p-datatable .p-datatable-tbody > tr > td span.red-cell) {
    color: #dc2626 !important;
    font-weight: 600;
}

/* Third table - diff-perc-finance-table */
::v-deep(.diff-perc-finance-table.p-datatable .p-datatable-thead > tr > th) {
    background-color: #0f1338 !important;
    color: white !important;
}

::v-deep(.diff-perc-finance-table.p-datatable .p-datatable-tbody > tr:nth-child(odd) > td) {
    background-color: #f3f4f6 !important;
    color: #111827 !important;
}

::v-deep(.diff-perc-finance-table.p-datatable .p-datatable-tbody > tr:nth-child(even) > td) {
    background-color: #ffffff !important;
    color: #111827 !important;
}

::v-deep(.diff-perc-finance-table.p-datatable .p-datatable-tbody > tr > td:first-child) {
    background-color: #0f1338 !important;
    color: white !important;
    font-weight: 600 !important;
    text-align: left !important;
}

::v-deep(.diff-perc-finance-table.p-datatable .p-datatable-tbody > tr > td span.green-cell) {
    color: #16a34a !important;
    font-weight: 600;
}

::v-deep(.diff-perc-finance-table.p-datatable .p-datatable-tbody > tr > td span.red-cell) {
    color: #dc2626 !important;
    font-weight: 600;
}

/* Dialog table - invoices-dialog-table */
::v-deep(.invoices-dialog-table.p-datatable .p-datatable-thead > tr > th) {
    background-color: #0f1338 !important;
    color: white !important;
}

::v-deep(.invoices-dialog-table.p-datatable .p-datatable-tbody > tr:not(:last-child):nth-child(odd) > td) {
    background-color: #f3f4f6 !important;
    color: #111827 !important;
}

::v-deep(.invoices-dialog-table.p-datatable .p-datatable-tbody > tr:not(:last-child):nth-child(even) > td) {
    background-color: #ffffff !important;
    color: #111827 !important;
}

::v-deep(.invoices-dialog-table.p-datatable .p-datatable-tbody > tr > td:first-child) {
    text-align: left !important;
}

::v-deep(.invoices-dialog-table.p-datatable .p-datatable-tbody > tr:not(:last-child) > td:first-child:has(.green-cell)) {
    color: #16a34a !important;
    font-weight: 600 !important;
}

::v-deep(.invoices-dialog-table.p-datatable .p-datatable-tbody > tr:not(:last-child) > td:first-child:has(.orange-cell)) {
    color: #d66400 !important;
    font-weight: 600 !important;
}

::v-deep(.invoices-dialog-table.p-datatable .p-datatable-tbody > tr:not(:last-child) > td:first-child:has(.red-cell)) {
    color: #ab0000 !important;
    font-weight: 600 !important;
}

::v-deep(.invoices-dialog-table.p-datatable .p-datatable-tbody > tr:last-child > td) {
    background-color: #0f1338 !important;
    color: white !important;
    font-weight: 600 !important;
}

::v-deep(.invoices-dialog-table.p-datatable .p-datatable-tbody > tr:last-child > td:first-child) {
    background-color: #0f1338 !important;
    color: white !important;
    font-weight: 600 !important;
    text-align: left !important;
}

/* ToggleButton */
::v-deep(.p-highlight) {
    background-color: #d4d4d8 !important;
}

::v-deep(.p-highlight:hover) {
    background-color: #a1a1aa !important;
}
</style>
<style>
/* Dark mode - First table */
.dark .main-finance-table.p-datatable .p-datatable-thead > tr > th {
    background-color: #D9D9D9 !important;
    color: #0f1338 !important;
}

.dark .main-finance-table.p-datatable .p-datatable-tbody > tr > td {
    color: white !important;
}

.dark .main-finance-table.p-datatable .p-datatable-tbody > tr:nth-child(odd) > td {
    background-color: #18181b !important;
    color: white !important;
}

.dark .main-finance-table.p-datatable .p-datatable-tbody > tr:nth-child(even) > td {
    background-color: #27272a !important;
    color: white !important;
}

.dark .main-finance-table.p-datatable .p-datatable-tbody > tr > td:first-child {
    background-color: #D9D9D9 !important;
    color: #0f1338 !important;
}

.dark #main-finance-table .p-datatable-tbody > tr:hover > td:not(:first-child) {
    background-color: rgb(63 63 70) !important;
    color: white !important;
}

/* Dark mode - Second table */
.dark .diff-finance-table.p-datatable .p-datatable-thead > tr > th {
    background-color: #D9D9D9 !important;
    color: #0f1338 !important;
}

.dark .diff-finance-table.p-datatable .p-datatable-tbody > tr > td {
    color: white !important;
}

.dark .diff-finance-table.p-datatable .p-datatable-tbody > tr:nth-child(odd) > td {
    background-color: #18181b !important;
    color: white !important;
}

.dark .diff-finance-table.p-datatable .p-datatable-tbody > tr:nth-child(even) > td {
    background-color: #27272a !important;
    color: white !important;
}

.dark .diff-finance-table.p-datatable .p-datatable-tbody > tr > td:first-child {
    background-color: #D9D9D9 !important;
    color: #0f1338 !important;
}

.dark .diff-finance-table.p-datatable .p-datatable-tbody > tr > td span.green-cell {
    color: #22c55e !important;
    font-weight: 600;
}

.dark .diff-finance-table.p-datatable .p-datatable-tbody > tr > td span.red-cell {
    color: #ef4444 !important;
    font-weight: 600;
}

/* Dark mode - Third table */
.dark .diff-perc-finance-table.p-datatable .p-datatable-thead > tr > th {
    background-color: #D9D9D9 !important;
    color: #0f1338 !important;
}

.dark .diff-perc-finance-table.p-datatable .p-datatable-tbody > tr > td {
    color: white !important;
}

.dark .diff-perc-finance-table.p-datatable .p-datatable-tbody > tr:nth-child(odd) > td {
    background-color: #18181b !important;
    color: white !important;
}

.dark .diff-perc-finance-table.p-datatable .p-datatable-tbody > tr:nth-child(even) > td {
    background-color: #27272a !important;
    color: white !important;
}

.dark .diff-perc-finance-table.p-datatable .p-datatable-tbody > tr > td:first-child {
    background-color: #D9D9D9 !important;
    color: #0f1338 !important;
}

.dark .diff-perc-finance-table.p-datatable .p-datatable-tbody > tr > td span.green-cell {
    color: #22c55e !important;
    font-weight: 600;
}

.dark .diff-perc-finance-table.p-datatable .p-datatable-tbody > tr > td span.red-cell {
    color: #ef4444 !important;
    font-weight: 600;
}

/* Dark Mode - Dialog Table */
.dark .invoices-dialog-table.p-datatable .p-datatable-thead > tr > th {
    background-color: #D9D9D9 !important;
    color: #0f1338 !important;
}

.dark .invoices-dialog-table.p-datatable .p-datatable-tbody > tr > td {
    color: white !important;
}

.dark .invoices-dialog-table.p-datatable .p-datatable-tbody > tr:not(:last-child):nth-child(odd) > td {
    background-color: #18181b !important;
    color: white !important;
}

.dark .invoices-dialog-table.p-datatable .p-datatable-tbody > tr:not(:last-child):nth-child(even) > td {
    background-color: #27272a !important;
    color: white !important;
}

.dark .invoices-dialog-table.p-datatable .p-datatable-tbody > tr:not(:last-child) > td:first-child:has(.green-cell) {
    /* background-color: #166534 !important; */
    color: #9bffbe !important;
}

.dark .invoices-dialog-table.p-datatable .p-datatable-tbody > tr:not(:last-child) > td:first-child:has(.orange-cell) {
    /* background-color: #9a3412 !important; */
    color: #ffc17b !important;
}

.dark .invoices-dialog-table.p-datatable .p-datatable-tbody > tr:not(:last-child) > td:first-child:has(.red-cell) {
    /* background-color: #991b1b !important; */
    color: #ff7070 !important;
}

.dark .invoices-dialog-table.p-datatable .p-datatable-tbody > tr:last-child > td {
    background-color: #D9D9D9 !important;
    color: #0f1338 !important;
}

.dark .invoices-dialog-table.p-datatable .p-datatable-tbody > tr:last-child > td:first-child {
    background-color: #D9D9D9 !important;
    color: #0f1338 !important;
}

/* ToggleButton dark mode */
.dark .p-highlight {
    background-color: #3f3f46 !important;
}

.dark .p-highlight:hover {
    background-color: #52525b !important;
}

/* ToggleButton hover and focus styles - light mode */
html:not(.dark) .p-togglebutton:not(.p-disabled):hover {
    border-color: #0f1338 !important;
}

html:not(.dark) .p-togglebutton.p-focus {
    border-color: #0f1338 !important;
    box-shadow: 0 0 0 0.1rem rgba(15, 19, 56, 0.8) !important;
}

html:not(.dark) .p-togglebutton.p-highlight {
    background-color: #0f1338 !important;
    border-color: #0f1338 !important;
    color: white !important;
}

html:not(.dark) .p-togglebutton.p-highlight:hover {
    background-color: #1a1f4d !important;
    border-color: #1a1f4d !important;
}

/* ToggleButton hover and focus styles - dark mode */
html.dark .p-togglebutton:not(.p-disabled):hover {
    border-color: #D9D9D9 !important;
}

html.dark .p-togglebutton.p-focus {
    border-color: #D9D9D9 !important;
    box-shadow: 0 0 0 0.1rem rgba(217, 217, 217, 0.5) !important;
}

html.dark .p-togglebutton.p-highlight {
    background-color: #D9D9D9 !important;
    border-color: #D9D9D9 !important;
    color: #0f1338 !important;
}

html.dark .p-togglebutton.p-highlight:hover {
    background-color: #c4c4c4 !important;
    border-color: #c4c4c4 !important;
}
</style>